import React, { useState, useEffect, useMemo } from 'react';
import { Container, Table, Badge, Alert, Spinner, Form, Button, InputGroup, Modal } from 'react-bootstrap';
import api from '../../api/axios';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNotification } from '../../contexts/NotificationContext';

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingExternalId, setEditingExternalId] = useState({});
  const [expandedOrders, setExpandedOrders] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [resendModal, setResendModal] = useState({ show: false, order: null, emails: '', sending: false });
  const { t } = useLanguage();
  const { notify } = useNotification();

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/orders');
      setOrders(response.data);
      setError(null);
    } catch (err) {
      setError(t('common.error'));
      console.error('Failed to fetch orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await api.put(`/api/admin/orders/${orderId}`, { status: newStatus });
      notify({ variant: 'success', message: t('common.success') });
      fetchOrders();
    } catch (err) {
      notify({
        variant: 'danger',
        message: err.response?.data?.error || t('common.error')
      });
      console.error('Failed to update order:', err);
    }
  };

  const updateExternalId = async (orderId) => {
    try {
      const externalId = editingExternalId[orderId];
      await api.put(`/api/admin/orders/${orderId}`, { external_id: externalId });
      notify({ variant: 'success', message: t('common.success') });
      fetchOrders();
      setEditingExternalId({});
    } catch (err) {
      notify({
        variant: 'danger',
        message: err.response?.data?.error || t('common.error')
      });
      console.error('Failed to update external ID:', err);
    }
  };

  const handleExternalIdChange = (orderId, value) => {
    setEditingExternalId({
      ...editingExternalId,
      [orderId]: value
    });
  };

  const toggleExpanded = (orderId) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'warning',
      confirmed: 'info',
      processing: 'primary',
      shipped: 'success',
      delivered: 'secondary',
      cancelled: 'danger'
    };
    
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const handleDownloadPDF = (pdfUrl, orderNumber) => {
    if (pdfUrl) {
      // If it's a relative URL, prepend the API base URL
      const fullUrl = pdfUrl.startsWith('http') 
        ? pdfUrl 
        : `${process.env.REACT_APP_API_URL || 'http://localhost:7000'}${pdfUrl}`;
      window.open(fullUrl, '_blank');
    }
  };

  const filteredOrders = useMemo(() => {
    const value = searchTerm.trim().toLowerCase();
    if (!value) {
      return orders;
    }

    return orders.filter((order) => {
      const haystack = [
        order.order_number,
        order.external_id,
        order.customer_name,
        order.username,
        order.email,
        order.company,
        order.status,
        order.additional_email
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(value);
    });
  }, [orders, searchTerm]);

  const openResendModal = (order) => {
    const defaultEmails = [order.email, order.additional_email].filter(Boolean).join(', ');
    setResendModal({ show: true, order, emails: defaultEmails, sending: false });
  };

  const closeResendModal = () => {
    setResendModal({ show: false, order: null, emails: '', sending: false });
  };

  const handleResendEmail = async () => {
    const emails = resendModal.emails.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);
    if (emails.length === 0) {
      notify({ variant: 'danger', message: t('admin.emailAddresses') });
      return;
    }
    setResendModal((prev) => ({ ...prev, sending: true }));
    try {
      await api.post(`/api/admin/orders/${resendModal.order.id}/resend`, { emails });
      notify({ variant: 'success', message: t('orders.resendSuccess') });
      closeResendModal();
    } catch (err) {
      notify({
        variant: 'danger',
        message: err.response?.data?.error || t('orders.resendFailed')
      });
      console.error('Failed to resend order email:', err);
      setResendModal((prev) => ({ ...prev, sending: false }));
    }
  };

  if (loading) {
    return (
      <Container className="loading-spinner">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <h1 className="mb-4">{t('admin.orders')}</h1>
      
      {error && <Alert variant="danger">{error}</Alert>}
      
      {filteredOrders.length === 0 ? (
        <Alert variant="info">{t('admin.noOrders')}</Alert>
      ) : (
        <>
          <InputGroup className="mb-3">
            <InputGroup.Text>🔍</InputGroup.Text>
            <Form.Control
              placeholder={`${t('admin.filterProducts')}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>

          <Table bordered hover responsive>
            <thead>
              <tr>
                <th style={{ width: '48px' }}></th>
                <th>{t('admin.orderNumber')}</th>
                <th>{t('admin.customer')}</th>
                <th>{t('admin.company')}</th>
                <th>{t('admin.date')}</th>
                <th>{t('admin.status')}</th>
                <th>{t('admin.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <React.Fragment key={order.id}>
                  <tr>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => toggleExpanded(order.id)}
                        aria-label="Toggle order details"
                      >
                        {expandedOrders[order.id] ? '−' : '+'}
                      </Button>
                    </td>
                    <td>{order.order_number}</td>
                    <td>{(order.customer_name || order.username)} ({order.email})</td>
                    <td>{order.company || t('common.na')}</td>
                    <td>{formatDate(order.created_at)}</td>
                    <td>{getStatusBadge(order.status)}</td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => openResendModal(order)}
                          title="Resend order confirmation"
                        >
                          ✉️ Resend
                        </Button>
                        {order.pdf_url && (
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => handleDownloadPDF(order.pdf_url, order.order_number)}
                            title="Download PDF"
                          >
                            📄 PDF
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandedOrders[order.id] && (
                    <tr>
                      <td colSpan="7">
                        <div className="p-3">
                          <div className="row mb-3">
                            <div className="col-md-6">
                              <strong>{t('admin.externalId')}:</strong>
                              <div className="d-flex gap-2 mt-1">
                                <Form.Control
                                  size="sm"
                                  type="text"
                                  placeholder={t('admin.externalId')}
                                  value={editingExternalId[order.id] !== undefined 
                                    ? editingExternalId[order.id] 
                                    : (order.external_id || '')}
                                  onChange={(e) => handleExternalIdChange(order.id, e.target.value)}
                                />
                                <Button
                                  size="sm"
                                  variant="primary"
                                  onClick={() => updateExternalId(order.id)}
                                  disabled={editingExternalId[order.id] === undefined}
                                >
                                  {t('admin.update')}
                                </Button>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <strong>{t('admin.additionalEmail')}</strong> {order.additional_email || t('common.na')}
                            </div>
                          </div>

                          <Table size="sm" bordered>
                            <thead>
                              <tr>
                                <th>{t('admin.name')}</th>
                                <th>Code</th>
                                <th>{t('admin.manufacturer')}</th>
                                <th>Model</th>
                                <th>{t('orders.columns.quantity')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {order.items.map((item) => (
                                <tr key={item.id}>
                                  <td>{item.product_name}</td>
                                  <td>{item.product_code || '-'}</td>
                                  <td>{item.manufacturer || '-'}</td>
                                  <td>{item.model || '-'}</td>
                                  <td>{item.quantity}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>

                          {order.notes && (
                            <div className="mb-2">
                              <strong>{t('admin.notes')}</strong> {order.notes}
                            </div>
                          )}

                          <div className="d-flex align-items-center gap-3">
                            <strong>{t('admin.status')}:</strong>
                            {getStatusBadge(order.status)}
                            <Form.Select
                              size="sm"
                              style={{ width: '200px' }}
                              value={order.status}
                              onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                            >
                              <option value="pending">Pending</option>
                              <option value="confirmed">Confirmed</option>
                              <option value="processing">Processing</option>
                              <option value="shipped">Shipped</option>
                              <option value="delivered">Delivered</option>
                              <option value="cancelled">Cancelled</option>
                            </Form.Select>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </Table>
        </>
      )}
      <Modal show={resendModal.show} onHide={closeResendModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>✉️ {t('admin.resendTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {resendModal.order && (
            <p className="text-muted mb-3">
              Order #{resendModal.order.order_number} — {(resendModal.order.customer_name || resendModal.order.username)}
            </p>
          )}
          <Form.Group>
            <Form.Label>{t('admin.emailAddresses')}</Form.Label>
            <Form.Control
              type="text"
              placeholder="email@example.com, another@example.com"
              value={resendModal.emails}
              onChange={(e) => setResendModal((prev) => ({ ...prev, emails: e.target.value }))}
              disabled={resendModal.sending}
              autoFocus
            />
            <Form.Text className="text-muted">{t('admin.emailSeparatorHint')}</Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeResendModal} disabled={resendModal.sending}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={handleResendEmail} disabled={resendModal.sending}>
            {resendModal.sending ? t('admin.sending') : t('admin.send')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminOrders;
